import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { API_BASE } from '../../config/api'

interface Review {
  id: string
  rating: number
  quote: string
  authorInitials: string
  authorName: string
  authorRole: string
}

const defaultReviews: Review[] = [
  {
    id: 'sarah-khan',
    rating: 5,
    quote: 'They transformed our entire digital presence. The attention to detail and technical expertise exceeded all our expectations.',
    authorInitials: 'SK',
    authorName: 'Sarah Khan',
    authorRole: 'CEO, Nova Retail',
  },
  {
    id: 'david-mwangi',
    rating: 5,
    quote: 'Rock-solid infrastructure with 99.99% uptime. Their backend engineering team is world-class.',
    authorInitials: 'DM',
    authorName: 'David Mwangi',
    authorRole: 'CTO, FinEdge',
  },
  {
    id: 'aisha-raza',
    rating: 5,
    quote: 'The app they built for us was beautifully designed and incredibly performant. Our users love it.',
    authorInitials: 'AR',
    authorName: 'Aisha Raza',
    authorRole: 'Founder, MediCare+',
  },
  {
    id: 'james-lee',
    rating: 5,
    quote: 'Professional, responsive, and delivered on time. They truly understand modern software development.',
    authorInitials: 'JL',
    authorName: 'James Lee',
    authorRole: 'PM, Atlas Logistics',
  },
]

interface ReviewsProps {
  reviews?: Review[]
}

export default function Reviews({ reviews = defaultReviews }: ReviewsProps) {
  const [liveReviews, setLiveReviews] = useState<Review[]>(reviews)
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [rating, setRating] = useState(5)
  const [hoveredRating, setHoveredRating] = useState<number | null>(null)
  const [name, setName] = useState('')
  const [role, setRole] = useState('')
  const [quote, setQuote] = useState('')
  
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  useEffect(() => {
    async function fetchReviews() {
      try {
        const res = await fetch(`${API_BASE}/api/v1/reviews`)
        if (!res.ok) throw new Error('API failed')
        const json = await res.json()
        if (json.success && Array.isArray(json.data) && json.data.length > 0) {
          setLiveReviews(json.data)
        }
      } catch (err) {
        console.error('Failed to fetch reviews:', err)
      }
    }
    fetchReviews()
  }, [])

  // Repeat reviews to ensure they span far wider than any screen resolution
  const repeatCount = Math.max(4, Math.ceil(16 / Math.max(1, liveReviews.length)))
  const safeRepeatCount = repeatCount % 2 === 0 ? repeatCount : repeatCount + 1
  const repeated = Array.from({ length: safeRepeatCount }).flatMap(() => liveReviews)

  const validateForm = () => {
    const nextErrors: { [key: string]: string } = {}
    if (!name.trim()) nextErrors.name = 'Name is required'
    if (!role.trim()) nextErrors.role = 'Role & Company is required'
    if (quote.trim().length < 10) nextErrors.quote = 'Review must be at least 10 characters long'
    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const handleReset = () => {
    setIsModalOpen(false)
    setName('')
    setRole('')
    setQuote('')
    setRating(5)
    setSubmitSuccess(false)
    setIsSubmitting(false)
    setSubmitError('')
    setErrors({})
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return

    setIsSubmitting(true)
    setSubmitError('')
    try {
      const response = await fetch(`${API_BASE}/api/v1/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          authorName: name,
          authorRole: role,
          rating,
          quote
        })
      })
      const json = await response.json()
      if (json.success) {
        setSubmitSuccess(true)
      } else {
        setSubmitError(json.error || 'Failed to submit review')
      }
    } catch (err) {
      setSubmitError('Network error. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section className="section-spacing" id="reviews">
      <div className="container-page">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-16">
          <div className="section-head" style={{ marginBottom: 0 }}>
            <span className="eyebrow">Reviews</span>
            <h2 className="mt-4" style={{ color: 'var(--text)' }}>What our clients say</h2>
          </div>
          
          <button
            onClick={() => setIsModalOpen(true)}
            className="btn-pill btn-ghost text-xs self-start sm:self-auto"
          >
            ✦ Write a review
          </button>
        </div>
      </div>

      <div className="reviews-container">
        <div className="reviews-track" style={{ animationDuration: `${75 * (safeRepeatCount / 2)}s` }}>
          {repeated.map((review, i) => (
            <div
              key={`${review.id}-${i}`}
              className="card-studio flex-shrink-0"
              style={{ width: '380px' }}
              aria-hidden={i >= reviews.length ? 'true' : undefined}
            >
              {/* Stars */}
              <div className="mb-3" style={{ color: 'var(--text)', letterSpacing: '4px', fontSize: '.9rem' }}>
                {'★'.repeat(review.rating)}
              </div>

              {/* Quote */}
              <p className="text-sm leading-relaxed mb-5" style={{ color: 'var(--text)' }}>
                "{review.quote}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-semibold font-display"
                  style={{ background: 'var(--surface-2)', color: 'var(--muted)' }}
                >
                  {review.authorInitials}
                </div>
                <div>
                  <div className="font-display text-sm font-semibold" style={{ color: 'var(--text)' }}>
                    {review.authorName}
                  </div>
                  <div className="text-xs" style={{ color: 'var(--muted)' }}>
                    {review.authorRole}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Review Modal Dialog */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="modal-backdrop" onClick={handleReset}>
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 15 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="modal-content p-8 md:p-10"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                className="absolute top-6 right-6 text-xl font-light cursor-pointer border-none bg-transparent"
                style={{ color: 'var(--muted)' }}
                onClick={handleReset}
                aria-label="Close modal"
              >
                ✕
              </button>

              <AnimatePresence mode="wait">
                {submitSuccess ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-center py-6"
                  >
                    <div
                      className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-6"
                      style={{ background: 'rgba(34, 197, 94, 0.08)', border: '1px solid var(--accent-green)' }}
                    >
                      <span className="text-lg" style={{ color: 'var(--accent-green)' }}>●</span>
                    </div>
                    <h3 className="font-display text-xl font-bold mb-3" style={{ color: 'var(--text)' }}>
                      Review Submitted!
                    </h3>
                    <p className="text-sm max-w-[340px] mx-auto mb-8" style={{ color: 'var(--muted)', lineHeight: 'relaxed' }}>
                      Thank you for sharing your feedback. Your review is pending moderation and will be published once approved.
                    </p>
                    <button
                      onClick={handleReset}
                      className="btn-pill btn-primary text-xs mx-auto py-3 px-8"
                    >
                      Close Window
                    </button>
                  </motion.div>
                ) : (
                  <motion.div
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <h3 className="font-display text-xl font-bold mb-1" style={{ color: 'var(--text)' }}>
                      Leave a Review
                    </h3>
                    <p className="text-xs mb-6" style={{ color: 'var(--muted)' }}>
                      Share your experience working with us.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-4">
                      {/* Name input */}
                      <div className="space-y-1">
                        <label className="form-label" htmlFor="clientName">Your Name</label>
                        <input
                          type="text"
                          id="clientName"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className="form-input text-sm py-3"
                          placeholder="e.g. John Doe"
                        />
                        {errors.name && (
                          <p className="text-xs text-red-500 font-mono">● {errors.name}</p>
                        )}
                      </div>

                      {/* Role & Company input */}
                      <div className="space-y-1">
                        <label className="form-label" htmlFor="clientRole">Role & Company</label>
                        <input
                          type="text"
                          id="clientRole"
                          value={role}
                          onChange={(e) => setRole(e.target.value)}
                          className="form-input text-sm py-3"
                          placeholder="e.g. CTO, TechCorp"
                        />
                        {errors.role && (
                          <p className="text-xs text-red-500 font-mono">● {errors.role}</p>
                        )}
                      </div>

                      {/* Interactive Rating */}
                      <div className="space-y-1">
                        <label className="form-label">Your Rating</label>
                        <div className="flex gap-1 items-center">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              type="button"
                              onClick={() => setRating(star)}
                              onMouseEnter={() => setHoveredRating(star)}
                              onMouseLeave={() => setHoveredRating(null)}
                              className={`star-interactive ${
                                (hoveredRating !== null ? star <= hoveredRating : star <= rating)
                                  ? 'active'
                                  : 'inactive'
                              }`}
                            >
                              ★
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Review Quote input */}
                      <div className="space-y-1">
                        <label className="form-label" htmlFor="clientQuote">Your Review</label>
                        <textarea
                          id="clientQuote"
                          value={quote}
                          onChange={(e) => setQuote(e.target.value)}
                          rows={4}
                          className="form-input text-sm resize-none"
                          placeholder="What was it like working with us? What did you build?"
                        />
                        {errors.quote && (
                          <p className="text-xs text-red-500 font-mono">● {errors.quote}</p>
                        )}
                      </div>

                      {submitError && (
                        <p className="text-xs text-red-500 font-mono">● {submitError}</p>
                      )}

                      {/* Actions */}
                      <div className="pt-4 flex justify-end gap-3">
                        <button
                          type="button"
                          onClick={handleReset}
                          className="btn-pill btn-ghost text-xs py-3"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="btn-pill btn-primary text-xs py-3"
                          style={!isSubmitting ? { background: 'var(--text)', color: 'var(--bg)' } : {}}
                        >
                          {isSubmitting ? 'Submitting...' : 'Submit Review'}
                        </button>
                      </div>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  )
}

